import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MobileShell, CustomerBottomNav } from "@/components/Shell";
import { ScanLine, ChevronRight, MapPin, User as UserIcon } from "lucide-react";
import { motion } from "framer-motion";

export default function Scan() {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [staff, setStaff] = useState([]);

  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data));
    api.get("/staff").then((r) => setStaff(r.data));
  }, []);

  return (
    <MobileShell>
      <div className="px-5 pt-10 pb-6">
        <Link to="/" className="text-xs text-[#A1A1AA] tracking-[0.2em] uppercase">← Zurück</Link>
        <h1 className="text-4xl font-black tracking-tight mt-6">QR-Code scannen</h1>
        <p className="text-[#A1A1AA] mt-3">Halte den QR-Code in den Rahmen — oder wähle direkt deine Location oder einen Mitarbeiter.</p>
      </div>

      {/* Scanner mock */}
      <div className="px-5">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="relative aspect-square max-w-sm mx-auto rounded-3xl overflow-hidden border border-white/10 bg-[#0d0d11]">
          <div className="absolute inset-0 flex items-center justify-center">
            <ScanLine className="w-16 h-16 text-[#EBB300]/30" strokeWidth={1} />
          </div>
          {/* Corner frames */}
          {["top-0 left-0 border-t-2 border-l-2", "top-0 right-0 border-t-2 border-r-2", "bottom-0 left-0 border-b-2 border-l-2", "bottom-0 right-0 border-b-2 border-r-2"].map((c, i) => (
            <div key={i} className={`absolute w-12 h-12 border-[#EBB300] rounded-${i === 0 ? "tl" : i === 1 ? "tr" : i === 2 ? "bl" : "br"}-xl ${c}`}></div>
          ))}
          {/* scan line */}
          <motion.div
            initial={{ y: 0 }}
            animate={{ y: ["0%", "100%", "0%"] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#EBB300] to-transparent gold-glow"
          />
          <div className="absolute bottom-4 left-0 right-0 text-center text-[10px] tracking-[0.3em] uppercase text-[#A1A1AA]">Scanner aktiv</div>
        </motion.div>
      </div>

      {/* Locations */}
      <section className="px-5 mt-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs tracking-[0.25em] uppercase text-[#EBB300]">Locations in deiner Nähe</h2>
          <MapPin className="w-4 h-4 text-[#A1A1AA]" />
        </div>
        <div className="space-y-2" data-testid="scan-locations-list">
          {locations.map((l) => (
            <button key={l.id} onClick={() => navigate(`/l/${l.id}`)} data-testid={`scan-location-${l.id}`}
              className="w-full flex items-center gap-4 bg-[#121216] border border-white/5 rounded-2xl p-4 hover:border-[#EBB300]/30 transition-colors text-left">
              {l.photo_url && <img src={l.photo_url} alt={l.name} className="w-12 h-12 rounded-xl object-cover" />}
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{l.name}</div>
                <div className="text-xs text-[#A1A1AA] truncate">{l.address}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-[#52525B]" />
            </button>
          ))}
        </div>
      </section>

      {/* Direct staff */}
      <section className="px-5 mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs tracking-[0.25em] uppercase text-[#EBB300]">Direkt zum Mitarbeiter</h2>
          <UserIcon className="w-4 h-4 text-[#A1A1AA]" />
        </div>
        <div className="grid grid-cols-2 gap-3" data-testid="scan-staff-list">
          {staff.map((s) => (
            <button key={s.id} onClick={() => navigate(`/s/${s.id}`)} data-testid={`scan-staff-${s.id}`}
              className="bg-[#121216] border border-white/5 rounded-2xl p-3 hover:border-[#EBB300]/30 transition-colors text-left">
              <img src={s.photo_url || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200"} alt={s.name} className="w-full aspect-square object-cover rounded-xl mb-3" />
              <div className="font-bold text-sm truncate">{s.name}</div>
              <div className="text-xs text-[#A1A1AA] truncate">{s.bio?.split('.')[0] || "Service"}</div>
            </button>
          ))}
        </div>
      </section>

      <CustomerBottomNav />
    </MobileShell>
  );
}
